import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ChartComponent } from '../../../../shared/components/chart/chart.component';
import { ApiRoot, ApiEndPoints } from '../../../../shared/constants/api-endpoints';
import { Utils } from '../../../../Utils/utils';
import { ApiService } from '../../../../core/services/api.service';
import { LookupKey } from '../../../../shared/constants/lookup-keys';
import { OptionFieldComponent } from '../../../../shared/components/controls/option-field/option-field.component';
import { InlineCalendarComponent } from '../../../../shared/components/inline-calendar/inline-calendar.component';
import { LookupService } from '../../../../core/services/lookup.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton-loader/skeleton-loader.component';

@Component({
  selector: 'app-dashboard-statics',
  imports: [ChartComponent, ReactiveFormsModule, TranslateModule, OptionFieldComponent, InlineCalendarComponent, SkeletonLoaderComponent],
  templateUrl: './dashboard-statics.component.html',
  styleUrl: './dashboard-statics.component.scss'
})
export class DashboardStaticsComponent implements OnChanges, OnInit {
  @Input() csn = '';
  @Input() loginName = '';
  lookupKey = LookupKey;
  filterForm: FormGroup;
  today: Date = new Date();
  showCustomDateRange = false;
  showDateFromCalendar = false;
  showDateToCalendar = false;
  selectedDateFrom: Date | null = null;
  selectedDateTo: Date | null = null;
  userType = '';
  serviceTypeOptions: any[] = [];

  noServiceSpendData = false;
  isServiceSpendLoading = false;
  private inFlightServiceSpendKey: string | null = null;
  private completedServiceSpendKey: string | null = null;

  serviceWiseSpendChartData: {
    title?: string | null;
    xAxisTitle?: string | null;
    yAxisTitle?: string | null;
    indexAxis?: 'x' | 'y';
    categories: string[];
    series: Array<{ name: string; color?: string; data: number[] }>;
    hideYAxisGridLines?: boolean;
    dashedXAxisGridLines?: boolean;
    isRtl?: boolean;
  } = {
    title: '',
    xAxisTitle: '',
    yAxisTitle: '',
    indexAxis: 'y',
    categories: [],
    series: []
  };

  constructor(
    private api: ApiService,
    private fb: FormBuilder,
    private lookupService: LookupService,
    private sharedService: SharedService
  ) {
    this.userType = this.sharedService.getPrimaryAccount()?.['Account Type Code'] ?? '';
    this.filterForm = this.fb.group({
      reportPeriod: ['Current year']
    });
  }

  ngOnInit(): void {
    this.loadServiceTypeOptions();
    this.loadServicewiseSpendData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['csn']) {
      this.loadServicewiseSpendData();
    }
  }

  onReportPeriodChange() {
    const period = this.filterForm.get('reportPeriod')?.value || 'Last 6 months';
    if (period === 'Custom') {
      this.showCustomDateRange = true;
      return;
    }

    this.showCustomDateRange = false;
    this.selectedDateFrom = null;
    this.selectedDateTo = null;
    this.loadServicewiseSpendData();
  }

  toggleDateCalendar(target: 'from' | 'to') {
    if (target === 'from') {
      this.showDateFromCalendar = !this.showDateFromCalendar;
      this.showDateToCalendar = false;
      return;
    }
    this.showDateToCalendar = !this.showDateToCalendar;
    this.showDateFromCalendar = false;
  }

  clearDateFrom(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateFrom = null;
    if (this.selectedDateTo) {
      this.selectedDateTo = null;
    }
    this.showDateFromCalendar = false;
    this.loadServicewiseSpendData();
  }

  clearDateTo(event?: MouseEvent) {
    event?.stopPropagation();
    this.selectedDateTo = null;
    this.showDateToCalendar = false;
    this.loadServicewiseSpendData();
  }

  onDateFromPicked(date: Date) {
    this.selectedDateFrom = date;
    this.showDateFromCalendar = false;
    if (this.selectedDateTo && this.selectedDateTo < date) {
      this.selectedDateTo = null;
      return;
    }
    this.loadServicewiseSpendData();
  }

  onDateToPicked(date: Date) {
    this.selectedDateTo = date;
    this.showDateToCalendar = false;
    this.loadServicewiseSpendData();
  }

  get dateFromDisplay(): string {
    return this.formatDateDisplay(this.selectedDateFrom);
  }

  get dateToDisplay(): string {
    return this.formatDateDisplay(this.selectedDateTo);
  }

  private loadServicewiseSpendData() {
    if (!this.csn) {
      this.isServiceSpendLoading = false;
      this.noServiceSpendData = true;
      this.updateServicewiseSpendChart([]);
      return;
    }

    if (this.showCustomDateRange && !this.selectedDateFrom) {
      this.isServiceSpendLoading = false;
      return;
    }

    const period = this.filterForm.get('reportPeriod')?.value;
    const range = this.getRequestDateRange(period);
    const payload = {
      account_number: this.csn,
      sr_cat_type_cd: '',
      login: "",//this.loginName || '',
      Period: period !== 'Custom' ? period : '',
      From_Date: range.fromDate,
      To_Date: range.toDate
    };
    const requestKey = `${payload.account_number}|${payload.Period}|${payload.From_Date}|${payload.To_Date}`;

    if (this.inFlightServiceSpendKey === requestKey || this.completedServiceSpendKey === requestKey) {
      return;
    }

    this.noServiceSpendData = false;
    this.isServiceSpendLoading = true;
    this.inFlightServiceSpendKey = requestKey;

    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_SERVICETYPEWISE_COUNT_REST, payload)
      .subscribe({
        next: (response: any) => {
          const records = this.getServicewiseSpendRows(response);
          this.noServiceSpendData = records.length === 0;
          this.updateServicewiseSpendChart(records);
          this.completedServiceSpendKey = requestKey;
          this.inFlightServiceSpendKey = null;
          this.isServiceSpendLoading = false;
        },
        error: () => {
          this.noServiceSpendData = true;
          this.updateServicewiseSpendChart([]);
          this.inFlightServiceSpendKey = null;
          this.isServiceSpendLoading = false;
        }
      });
  }

  private updateServicewiseSpendChart(records: any[]) {
    const rows = records
      .map((row) => {
        const serviceName = this.getValueFromRow(row, [
          'ServiceType'
        ]);
        const amountRaw = this.getValueFromRow(row, [
          'SRCount'
        ]);
        const amount = Number(amountRaw);
        return serviceName
          ? { serviceName, amount: Number.isFinite(amount) ? amount : 0 }
          : null;
      })
      .filter((item: { serviceName: string; amount: number } | null) => !!item) as Array<{
        serviceName: string;
        amount: number;
      }>;

    this.serviceWiseSpendChartData = {
      title: '',
      xAxisTitle: '',
      yAxisTitle: '',
      indexAxis: 'y',
      categories: rows.map((row) => this.getMappedServiceTypeLabel(row.serviceName)),
      series: [
        {
          name: 'Count',
          color: '#3D89D8',
          data: rows.map((row) => row.amount)
        }
      ],
      hideYAxisGridLines: true,
      dashedXAxisGridLines: true,
      isRtl: this.sharedService.getCurrentLang() === 'ar'
    };
  }

  private getServicewiseSpendRows(response: any): any[] {
    const candidates = [
      response?.GetServicetypewiseCountOutput,
      response?.GetServicewiseSpendAmtOutput,
      response?.servicewiseSpendAmt,
      response?.serviceWiseSpendAmt,
      response?.servicewiseSpendAmount,
      response?.serviceSpend,
      response?.spendingAmount,
      response?.data
    ];

    for (const candidate of candidates) {
      const list = Utils.checkIsArray(candidate);
      if (list.length) {
        return list;
      }
    }

    return [];
  }

  private loadServiceTypeOptions() {
    this.lookupService
      .post<any>(LookupKey.LOOKUP_URL, {
        ProcessName: 'DC Get List Of Values WF',
        Type: LookupKey.DC_SR_TYPE,
        ParentValue: this.userType || ''
      })
      .subscribe({
        next: (result: any) => {
          this.serviceTypeOptions = Utils.checkIsArray(result?.SiebelMessage?.['List Of Values']);
        },
        error: () => {
          this.serviceTypeOptions = [];
        }
      });
  }

  private getMappedServiceTypeLabel(serviceType: string): string {
    const source = this.normalizeText(serviceType);
    if (!source) {
      return serviceType;
    }

    const match = this.serviceTypeOptions.find((item: any) => {
      const name = this.normalizeText(item?.Name);
      const value = this.normalizeText(item?.Value);
      return name === source || value === source;
    });

    return match?.Value || serviceType;
  }

  private getLastSixMonthsRange(): { fromDate: string; toDate: string } {
    const now = new Date();
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    return {
      fromDate: this.formatDate(from),
      toDate: this.formatDate(to)
    };
  }

  private getPeriodDateRange(period: string): { fromDate: string; toDate: string } {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (period === 'Last year') {
      const targetYear = year - 1;
      return {
        fromDate: this.formatDate(new Date(targetYear, 0, 1)),
        toDate: this.formatDate(new Date(targetYear, 11, 31))
      };
    }

    if (period === 'Current year') {
      return {
        fromDate: this.formatDate(new Date(year, 0, 1)),
        toDate: this.formatDate(new Date(year, 11, 31))
      };
    }

    if (period === 'Previous month') {
      const from = new Date(year, month - 1, 1);
      const to = new Date(year, month, 0);
      return {
        fromDate: this.formatDate(from),
        toDate: this.formatDate(to)
      };
    }

    if (period === 'Current month') {
      const from = new Date(year, month, 1);
      const to = new Date(year, month + 1, 0);
      return {
        fromDate: this.formatDate(from),
        toDate: this.formatDate(to)
      };
    }

    return this.getLastSixMonthsRange();
  }

  private getRequestDateRange(period: string): { fromDate: string; toDate: string } {
    if (this.showCustomDateRange && this.selectedDateFrom) {
      return {
        fromDate: this.formatDate(this.selectedDateFrom),
        toDate: this.formatDate(this.selectedDateTo ?? this.today)
      };
    }
    return this.getPeriodDateRange(period);
  }

  private formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatDateDisplay(date: Date | null): string {
    if (!date) {
      return '';
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private getValueFromRow(row: any, keys: string[]): string {
    for (const key of keys) {
      if (row?.[key] != null) {
        return String(row[key]).trim();
      }
    }
    return '';
  }

  private normalizeText(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }
}
